import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the AlertFooter module
 */
export default class AlertFooter extends MessageBus {
  /**
   * Constructor function for the AlertFooter class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.KEY = 'care-footer-alert-hidden';

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners.
   * @returns {object} AlertFooter A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   * @returns {object} AlertFooter A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.alertFooter = this.element;
    this.closeButtons = this.element.querySelectorAll(
      `.${CLASS_NAMES.ALERT_FOOTER_CLOSE}`
    );
    return this;
  }

  /**
   * Setup event handlers
   * @returns {object} AlertFooter A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleCloseClick = this.handleCloseClick.bind(this);
    return this;
  }

  /**
   * Enable event listeners and handle session storage
   * @returns {object} AlertFooter A reference to the current instance of the class
   * @chainable
   */
  enable() {
    const isHidden = sessionStorage.getItem(this.KEY);

    if (isHidden) {
      this.hideAlert(); // Keeps it hidden
      this.handleResize();
    } else {
      this.showAlert(); // Only show if not hidden
      this.handleResize();
    }
    if (this.closeButtons.length > 0) {
      this.closeButtons.forEach((button) => {
        button.addEventListener(EVENTS.CLICK, this.handleCloseClick);
      });
    }
    window.addEventListener(EVENTS.RESIZE, this.handleResize.bind(this));
    window.addEventListener(EVENTS.SCROLL, this.handleResize.bind(this));
    window.addEventListener(EVENTS.LOAD, this.handleResize.bind(this));
    return this;
  }

  /**
   * Handles close button click event
   */
  handleCloseClick() {
    const alertFooterHeight = this.alertFooter.scrollHeight || 0;
    this.handleResize();
    this.hideAlert();
    window.scroll({
      top: window.scrollY - `${alertFooterHeight}`,
      behavior: 'smooth'
    });
    sessionStorage.setItem(this.KEY, true);
    document.body.classList.remove(CLASS_NAMES.ALERT_FOOTER_ACTIVE);
  }

  /**
   * Hides the alert footer
   */
  hideAlert() {
    if (this.alertFooter) {
      this.alertFooter.classList.remove(CLASS_NAMES.IS_VISIBLE);
    }

    document.body.classList.remove(CLASS_NAMES.ALERT_FOOTER_ACTIVE);
  }

  /**
   * Shows the alert footer
   */
  showAlert() {
    if (this.alertFooter) {
      this.alertFooter.classList.add(CLASS_NAMES.IS_VISIBLE);
    }
  }

  handleResize() {
    if (this.alertFooter) {
      const bodyHeight =
        document.offsetHeight || document.documentElement.offsetHeight || 0;
      document.body.style.setProperty('--height-body', `${bodyHeight}px`);
      document.body.classList.add('has-height-body');
    }
  }
}
